// Hook for cell operations
import {
  handleCellClick as handleCellClickUtil,
  handleWordClick as handleWordClickUtil,
  startCellEdit as startCellEditUtil,
  saveCellEdit as saveCellEditUtil,
  cancelCellEdit as cancelCellEditUtil,
  handlePdfClick as handlePdfClickUtil,
} from "../utils/cellEditingUtils";
import { useCallback } from "react";

/**
 * Hook for cell operations - provides interface to cell manipulation
 */
const useCellOperations = (
  tableState,
  pdfState,
  editingState,
  highlightWords,
  refs,
  getCanvasCoordinates,
  convertCanvasToPdfCoordinates,
  findCellAtPdfCoordinates
) => {
  const { tableData, selectedTable, selectedCell } = tableState;
  const { currentPage } = pdfState;
  const { editingCell, editValue } = editingState;

  const handleCellClick = useCallback(
    (rowIndex, colIndex, cellData) => {
      handleCellClickUtil(rowIndex, colIndex, {
        editingCell,
        cancelCellEdit: () =>
          cancelCellEditUtil({
            setEditingCell: editingState.setEditingCell,
            setEditValue: editingState.setEditValue,
          }),
        tableData,
        selectedTable,
        setSelectedCell: tableState.setSelectedCell,
        setSelectedWordIndex: pdfState.setSelectedWordIndex,
        currentPage,
        setCurrentPage: pdfState.setCurrentPage,
        highlightWordsOnPdf: highlightWords,
      });
    },
    [
      editingCell,
      tableData,
      selectedTable,
      currentPage,
      highlightWords,
      tableState,
      pdfState,
      editingState,
    ]
  );

  const handleWordClick = useCallback(
    (wordIndex) => {
      handleWordClickUtil(wordIndex, {
        setSelectedWordIndex: pdfState.setSelectedWordIndex,
        selectedCell,
        currentPage,
        setCurrentPage: pdfState.setCurrentPage,
        highlightWordsOnPdf: highlightWords,
      });
    },
    [selectedCell, currentPage, highlightWords, pdfState]
  );

  const startCellEdit = useCallback(() => {
    startCellEditUtil(selectedCell, {
      setEditingCell: editingState.setEditingCell,
      setEditValue: editingState.setEditValue,
    });
  }, [selectedCell, editingState]);

  const saveCellEdit = useCallback(() => {
    saveCellEditUtil({
      editingCell,
      selectedTable,
      tableData,
      editValue,
      setTableData: tableState.setTableData,
      setSelectedCell: tableState.setSelectedCell,
      setEditingCell: editingState.setEditingCell,
      setEditValue: editingState.setEditValue,
    });
  }, [
    editingCell,
    selectedTable,
    tableData,
    editValue,
    tableState,
    editingState,
  ]);

  const cancelCellEdit = useCallback(() => {
    cancelCellEditUtil({
      setEditingCell: editingState.setEditingCell,
      setEditValue: editingState.setEditValue,
    });
  }, [editingState]);

  const handlePdfClick = useCallback(
    (event) => {
      return handlePdfClickUtil(event, {
        canvasRef: refs.canvasRef,
        highlightContainerRef: refs.highlightContainerRef,
        pdfPageDimensions: refs.pdfPageDimensions,
        selectedTable,
        tableData,
        currentPage,
        getCanvasCoordinates,
        convertCanvasToPdfCoordinates,
        findCellAtPdfCoordinates,
        handleCellClick,
        // Add all the parameters that handleCellClick needs
        editingCell,
        cancelCellEdit: () =>
          cancelCellEditUtil({
            setEditingCell: editingState.setEditingCell,
            setEditValue: editingState.setEditValue,
          }),
        setSelectedCell: tableState.setSelectedCell,
        setSelectedWordIndex: pdfState.setSelectedWordIndex,
        setCurrentPage: pdfState.setCurrentPage,
        highlightWordsOnPdf: highlightWords,
      });
    },
    [
      refs,
      selectedTable,
      tableData,
      currentPage,
      getCanvasCoordinates,
      convertCanvasToPdfCoordinates,
      findCellAtPdfCoordinates,
      handleCellClick,
      editingCell,
      editingState,
      tableState,
      pdfState,
      highlightWords,
    ]
  );

  return {
    handleCellClick,
    handleWordClick,
    startCellEdit,
    saveCellEdit,
    cancelCellEdit,
    handlePdfClick,
  };
};

export default useCellOperations;
